/**
 * Brand strings, in one place.
 *
 * Every user-visible occurrence of the product name goes through here, so a
 * future rename is a one-file change rather than a search across components.
 */
export const BRAND_NAME = 'EntityLinks';

/**
 * The legal entity behind the trading name, and the seller of record.
 *
 * **A single constant, on purpose** (operator ruling D-2), and on 22 September
 * 2026 that paid off exactly as intended: the Ltd → LLC change the comment
 * below anticipated arrived as WAIT-A **P-3**, and it was this line plus the
 * matching one in `server/src/brand.ts`.
 *
 * **EdgeWorks LLC is who takes the money.** The Stripe account the portal
 * charges through belongs to it, so it is the seller a customer is contracting
 * with and the name that has to appear on the Terms. The card statement still
 * reads EDGEWORKS LLC and is **P-2**, open: that is a Stripe dashboard setting
 * rather than a constant, and it is the one place this name and the brand can
 * still disagree.
 */
export const BRAND_LEGAL_ENTITY = 'EdgeWorks LLC';

/**
 * Trading name and legal entity together.
 *
 * Removed from every page footer in round 1 (ruling D-2) — it now appears on
 * the Terms page, which is where someone actually looking for it goes.
 * Transactional emails keep it; that requirement did not change.
 */
export const BRAND_LEGAL_LINE = `${BRAND_NAME}, a trading name of ${BRAND_LEGAL_ENTITY}`;

export const BRAND_DOMAIN = 'entitylinks.com';

/**
 * Where to reach us - **the only contact address this portal shows**.
 *
 * D-15, ruled 22 September: `info@entitylinks.com` and nothing else. It was
 * `hello@`, which was never wrong but was one of several the business could
 * have used; one address is a promise somebody can keep, and a guard test
 * refuses any other in shipped output.
 */
export const BRAND_CONTACT_EMAIL = `info@${BRAND_DOMAIN}`;

/**
 * The website's legal pages, which are **the master** (D-15).
 *
 * The portal used to carry its own Terms and Privacy. They are the website's
 * now: one set of words, maintained where the lawyers and the marketing copy
 * already live, rather than two that drift. The portal's own routes survive as
 * a line and a link so an old bookmark still lands somewhere useful.
 */
export const LEGAL_URLS = {
  terms: `https://${BRAND_DOMAIN}/legal/terms/`,
  privacy: `https://${BRAND_DOMAIN}/legal/privacy/`,
  refunds: `https://${BRAND_DOMAIN}/legal/refunds/`,
} as const;

/**
 * **Every link that leaves the portal opens beside it, not instead of it** -
 * Craig's recheck of 24 September.
 *
 * The cost this pays for is specific and was the reason it was raised: the
 * sign-up line sits under a half-filled form. Somebody who stops to read the
 * Terms before creating an account and comes back to a blank page has been
 * punished for reading them, and the portal is the one that asked them to.
 * The set-password page is worse again, because the link that got them there
 * is single-use.
 *
 * **`rel` is not decoration.** `noopener` denies the opened page a handle on
 * `window.opener`, which is what stops it navigating the tab it came from;
 * `noreferrer` is named as well because the two are separate switches in older
 * browsers and the pairing costs nothing. Both are our own site today, which is
 * exactly when it is cheap to make the habit.
 *
 * Spread rather than wrapped in a component, so `ErrorBoundary` keeps the
 * independence its doc comment claims: this is two string literals, and it
 * imports nothing.
 */
export const NEW_TAB = { target: '_blank', rel: 'noopener noreferrer' } as const;
